import * as Sentry from '@sentry/react';
import type { Envelope, EnvelopeItemType, Event } from '@sentry/core';

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

const EXTRA_KEY = 'ROUTE_TO';

interface MatchParam {
  /** The envelope to be sent */
  envelope: Envelope;
  /**
   * A function that returns an event from the envelope if one exists. You can optionally pass an array of envelope item
   * types to filter by - only envelopes matching the given types will be multiplexed.
   * Allowed values are: 'event', 'transaction', 'profile', 'replay_event'
   *
   * @param types Defaults to ['event']
   */
  getEvent(types?: EnvelopeItemType[]): Event | undefined;
}
type RouteTo = {
  dsn: string;
  release: string;
};

/**
 * Специальный транспорт, который позволяет отсылать ошибки в разные DSN.
 * Например, мы поймали ошибку из шапки и они указали свой DSN (вытягивается ниже в beforeSend), тогда ошибка уйдёт им, а не нам.
 *
 * https://docs.sentry.io/platforms/javascript/best-practices/micro-frontends/
 */
const transport = Sentry.makeMultiplexedTransport(
  Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  (args: MatchParam): RouteTo[] => {
    const event = args.getEvent();

    if (event?.extra && EXTRA_KEY in event.extra && Array.isArray(event.extra[EXTRA_KEY])) {
      const routeTo = event.extra[EXTRA_KEY] as RouteTo[];
      if (routeTo[0]?.dsn && routeTo[0]?.release) {
        return [{ dsn: routeTo[0].dsn, release: routeTo[0].release }];
      }
    }

    return [];
  },
);

Sentry.init({
  dsn: '__SENTRY_DSN__',
  environment: '__SENTRY_ENVIRONMENT__',
  release: process.env.SENTRY_RELEASE,

  // Add optional integrations for additional features
  integrations: [Sentry.replayIntegration(), Sentry.moduleMetadataIntegration()],

  // Как часто слать ошибки, 1 - всегда (100%), 0 - никогда (0%)
  sampleRate: 0.5,

  // Как часто слать трассировку, 1 - всегда (100%), 0 - никогда (0%)
  tracesSampleRate: 0.5,

  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  /**
   * https://docs.sentry.io/platforms/javascript/best-practices/micro-frontends/
   */
  transport,
  beforeSend: (event) => {
    if (event?.exception?.values?.[0].stacktrace?.frames) {
      const { frames } = event.exception.values[0].stacktrace;

      // Найдём последний фрейм, где есть информация с dsn
      const routeTo = frames
        .filter((frame) => frame.module_metadata?.dsn && frame.module_metadata?.release)
        .map((frame) => frame.module_metadata)
        .slice(-1);

      if (routeTo.length) {
        // eslint-disable-next-line no-param-reassign
        event.extra = {
          ...event.extra,
          [EXTRA_KEY]: routeTo,
        };

        return event;
      }
    }

    // Похоже, это не наша ошибка и неизвестно чья
    // null значит не отсылать
    return null;
  },
});
